import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Heart, User, ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const STORES = [
  { key: "jewelry", label: "JEWELRY" },
  { key: "men", label: "ÉVAREN MEN" },
  { key: "women", label: "ÉVAREN WOMEN" },
];

export default function Header({ onOpenCountry, onOpenSearch }) {
  const { storefront, setStorefront, country, currency, cartCount, wishlist, setCartOpen, user } = useApp();
  const navigate = useNavigate();
  const [bootstrap, setBootstrap] = useState({ storefront: null, navigation: [] });
  const [hovered, setHovered] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    api.get(`/storefront/${storefront}/bootstrap`).then((r) => setBootstrap(r.data)).catch(() => {});
  }, [storefront]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const switchStore = (key) => {
    setStorefront(key);
    setHovered(null);
    setMobileOpen(false);
    navigate("/");
  };

  const logo = bootstrap.storefront || {};
  const nav = bootstrap.navigation || [];

  return (
    <header className="sticky top-0 z-50" style={{ background: "var(--ev-surface)" }} onMouseLeave={() => setHovered(null)}>
      {/* Storefront switcher */}
      <div style={{ background: "#0F2F24" }}>
        <div className="max-w-[1500px] mx-auto flex items-end justify-center gap-1.5 h-11 px-6">
          {STORES.map((s) => {
            const active = storefront === s.key;
            return (
              <button key={s.key} data-testid={`switcher-${s.key}`} onClick={() => switchStore(s.key)}
                className="text-[0.62rem] tracking-[0.22em] font-semibold transition-all duration-300 px-6 py-2"
                style={active
                  ? { background: "#F3EBDD", color: "#0F2F24", borderRadius: "5px 5px 0 0" }
                  : { color: "rgba(243,235,221,0.55)" }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = "#F3EBDD"; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = "rgba(243,235,221,0.55)"; }}>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Announcement */}
      {bootstrap.content?.announcement && (
        <div className="text-center py-2 text-[0.62rem] tracking-[0.2em] uppercase" style={{ background: "var(--ev-bg)", color: "var(--ev-muted)" }}>
          {bootstrap.content.announcement}
        </div>
      )}

      {/* Main bar */}
      <div className="max-w-[1500px] mx-auto px-6 grid grid-cols-3 items-center" style={{ height: scrolled ? 76 : 96, transition: "height 0.4s ease" }}>
        <div className="flex items-center gap-5">
          <button data-testid="mobile-menu-btn" className="lg:hidden" onClick={() => setMobileOpen(true)}>
            <Menu size={22} strokeWidth={1.2} />
          </button>
          <button data-testid="header-country-btn" onClick={onOpenCountry}
            className="hidden lg:flex items-center gap-2 text-[0.68rem] tracking-[0.15em] uppercase ev-link">
            {country ? country.name : "Select Country"} · {currency}
          </button>
        </div>

        <button data-testid="header-logo" onClick={() => navigate("/")} className="flex flex-col items-center justify-center">
          <span className="font-serif leading-none whitespace-nowrap" style={{ fontSize: scrolled ? "1.25rem" : "1.6rem", letterSpacing: "0.14em", color: "#0F2F24", transition: "font-size 0.4s ease" }}>
            {logo.logo_text || "MAISON ÉVAREN"}
          </span>
          {logo.logo_subtext && (
            <span className="text-[0.55rem] tracking-[0.5em] mt-1.5" style={{ color: "var(--ev-muted)" }}>{logo.logo_subtext}</span>
          )}
        </button>

        <div className="flex items-center justify-end gap-5">
          <button data-testid="header-search" onClick={onOpenSearch}><Search size={19} strokeWidth={1.2} /></button>
          <button data-testid="header-wishlist" onClick={() => navigate("/wishlist")} className="relative hidden sm:block">
            <Heart size={19} strokeWidth={1.2} />
            {wishlist.length > 0 && <span className="absolute -top-1.5 -right-1.5 text-[0.55rem] w-3.5 h-3.5 flex items-center justify-center rounded-full text-white" style={{ background: "var(--ev-accent)" }}>{wishlist.length}</span>}
          </button>
          <button data-testid="header-account" onClick={() => navigate(user ? "/account" : "/login")}><User size={19} strokeWidth={1.2} /></button>
          <button data-testid="header-cart" onClick={() => setCartOpen(true)} className="relative">
            <ShoppingBag size={19} strokeWidth={1.2} />
            {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 text-[0.55rem] w-3.5 h-3.5 flex items-center justify-center rounded-full text-white" style={{ background: "var(--ev-accent)" }}>{cartCount}</span>}
          </button>
        </div>
      </div>

      {/* Nav */}
      <nav className="hidden lg:block border-t" style={{ borderColor: "var(--ev-border)" }}>
        <div className="max-w-[1500px] mx-auto px-6 flex items-center justify-center gap-11 h-12">
          {nav.map((item, i) => (
            <button key={i} data-testid={`nav-${item.label}`} onMouseEnter={() => setHovered(i)}
              onClick={() => navigate(item.href)}
              className="text-[0.7rem] tracking-[0.2em] uppercase font-medium py-4 transition-colors"
              style={{ color: hovered === i ? "var(--ev-accent)" : "var(--ev-text)" }}>
              {item.label}
            </button>
          ))}
        </div>

        {/* Mega menu */}
        <AnimatePresence>
          {hovered !== null && nav[hovered] && (nav[hovered].columns?.length > 0 || nav[hovered].image) && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="absolute left-0 right-0 border-t border-b shadow-xl"
              style={{ background: "rgba(255,255,255,0.98)", backdropFilter: "blur(12px)", borderColor: "var(--ev-border)" }}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="max-w-[1500px] mx-auto px-10 py-12 grid grid-cols-4 gap-10">
                <div className="col-span-2 grid grid-cols-2 gap-8">
                  {(nav[hovered].columns || []).map((col, ci) => (
                    <div key={ci}>
                      <div className="eyebrow mb-5" style={{ color: "var(--ev-muted)" }}>{col.heading}</div>
                      <ul className="space-y-3">
                        {(col.links || []).map((l, li) => (
                          <li key={li}>
                            <button onClick={() => { navigate(l.href); setHovered(null); }}
                              className="font-serif text-lg ev-link" data-testid={`mega-${l.label}`}>{l.label}</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                {nav[hovered].image && (
                  <div className="col-start-3 col-span-2">
                    <button onClick={() => { navigate(nav[hovered].href); setHovered(null); }} className="img-zoom block relative w-full h-64">
                      <img src={nav[hovered].image} alt={nav[hovered].image_label} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-end p-6 bg-gradient-to-t from-black/50 to-transparent">
                        <span className="font-serif text-2xl text-white italic">{nav[hovered].image_label}</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-[60] lg:hidden" onClick={() => setMobileOpen(false)} />
            <motion.div initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
              className="fixed top-0 left-0 bottom-0 w-[85%] max-w-sm z-[70] lg:hidden overflow-y-auto"
              style={{ background: "var(--ev-surface)" }}>
              <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: "var(--ev-border)" }}>
                <span className="font-serif text-lg">{logo.logo_text || "MAISON ÉVAREN"} <span className="text-xs tracking-widest">{logo.logo_subtext}</span></span>
                <button data-testid="mobile-close" onClick={() => setMobileOpen(false)}><X size={22} strokeWidth={1.2} /></button>
              </div>
              <div className="flex border-b" style={{ borderColor: "var(--ev-border)" }}>
                {STORES.map((s) => (
                  <button key={s.key} onClick={() => switchStore(s.key)}
                    className="flex-1 py-3 text-[0.55rem] tracking-[0.15em] font-medium"
                    style={{ background: storefront === s.key ? "var(--ev-text)" : "transparent", color: storefront === s.key ? "#fff" : "var(--ev-muted)" }}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="p-6 space-y-1">
                {nav.map((item, i) => (
                  <button key={i} onClick={() => { navigate(item.href); setMobileOpen(false); }}
                    className="block w-full text-left font-serif text-2xl py-3 border-b" style={{ borderColor: "var(--ev-border)" }}>
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="p-6 space-y-4 text-sm">
                <button onClick={() => { onOpenCountry(); setMobileOpen(false); }} className="block ev-link">{country ? country.name : "Select Country"} · {currency}</button>
                <button onClick={() => { navigate("/wishlist"); setMobileOpen(false); }} className="block ev-link">Wishlist ({wishlist.length})</button>
                <button onClick={() => { navigate(user ? "/account" : "/login"); setMobileOpen(false); }} className="block ev-link">{user ? "My Account" : "Sign In"}</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
